/**
 * Modecraze Wishlist - localStorage storage layer
 * Step 3: guest-only. Customer metafield sync added in Step 4.
 */
window.Wishlist = (function() {
  const STORAGE_KEY = 'modecraze_wishlist';
  const MAX_ITEMS = 100;

  function read() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.warn('[Wishlist] localStorage read failed:', e);
      return [];
    }
  }

  function write(items) {
    // FIFO cap: drop oldest if over limit
    if (items.length > MAX_ITEMS) {
      items.sort((a, b) => a.addedAt - b.addedAt);
      items = items.slice(items.length - MAX_ITEMS);
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.warn('[Wishlist] localStorage write failed:', e);
    }
    return items;
  }

  function getAll() {
    return read();
  }

  function count() {
    return read().length;
  }

  /**
   * Check if a product is in the wishlist (in any form: product-level OR any variant).
   * Used by product card hearts.
   */
  function hasProduct(productId) {
    productId = String(productId);
    return read().some(item => String(item.productId) === productId);
  }

  /**
   * Check if a specific variant is in the wishlist (exact match).
   * Used by PDP heart when a variant is selected.
   */
  function hasVariant(productId, variantId) {
    productId = String(productId);
    variantId = String(variantId);
    return read().some(item =>
      String(item.productId) === productId &&
      String(item.variantId) === variantId
    );
  }

  /**
   * Check if PDP heart should be active for the current product+variant context.
   * Active if: product-level entry exists OR this specific variant is wishlisted.
   */
  function isPdpActive(productId, variantId) {
    productId = String(productId);
    variantId = variantId ? String(variantId) : null;
    return read().some(item => {
      if (String(item.productId) !== productId) return false;
      // Product-level entry matches any variant context
      if (item.variantId === null) return true;
      // Variant-level entry must match exactly
      return variantId && String(item.variantId) === variantId;
    });
  }

  /**
   * Add product-level (from card). No-op if product already in any form.
   */
  function addProduct({ productId, handle }) {
    if (hasProduct(productId)) return read();
    const items = read();
    items.push({
      productId: Number(productId),
      variantId: null,
      handle: handle,
      addedAt: Date.now()
    });
    return write(items);
  }

  /**
   * Add variant-level (from PDP). Upgrades product-level entry if one exists.
   */
  function addVariant({ productId, variantId, handle }) {
    if (hasVariant(productId, variantId)) return read();
    let items = read();
    // Remove product-level entry for this product if it exists (upgrade behavior)
    items = items.filter(item =>
      !(String(item.productId) === String(productId) && item.variantId === null)
    );
    items.push({
      productId: Number(productId),
      variantId: Number(variantId),
      handle: handle,
      addedAt: Date.now()
    });
    return write(items);
  }

  /**
   * Remove all entries for a product (used by card heart).
   */
  function removeProduct(productId) {
    productId = String(productId);
    const items = read().filter(item => String(item.productId) !== productId);
    return write(items);
  }

  /**
   * Remove a specific variant entry (used by PDP heart when variant-pinned).
   * If no variant-level entries remain and a product-level entry exists, remove that too.
   */
  function removeVariant(productId, variantId) {
    productId = String(productId);
    variantId = String(variantId);
    const items = read().filter(item => {
      if (String(item.productId) !== productId) return true;
      // Remove variant-level match
      if (item.variantId !== null && String(item.variantId) === variantId) return false;
      // Remove product-level entry for this product (PDP heart toggle clears it)
      if (item.variantId === null) return false;
      return true;
    });
    return write(items);
  }

  /**
   * Clear everything. Used on logout.
   */
  function clear() {
    return write([]);
  }

  return {
    getAll,
    count,
    hasProduct,
    hasVariant,
    isPdpActive,
    addProduct,
    addVariant,
    removeProduct,
    removeVariant,
    clear
  };
})();

/**
 * UI layer: heart buttons on cards and PDP.
 */
(function() {
  const SELECTOR = '[data-wishlist-btn]';

  function updateButton(btn) {
    const productId = btn.dataset.productId;
    const variantId = btn.dataset.variantId;
    const context = btn.dataset.context;

    let active;
    if (context === 'pdp') {
      active = Wishlist.isPdpActive(productId, variantId || null);
    } else {
      active = Wishlist.hasProduct(productId);
    }

    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    btn.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
  }

  function updateAllButtons() {
    document.querySelectorAll(SELECTOR).forEach(updateButton);
  }

 function handleClick(e) {
  const btn = e.target.closest(SELECTOR);
  if (!btn) return;
  e.preventDefault();
  e.stopPropagation();

  const productId = btn.dataset.productId;
  const handle = btn.dataset.productHandle;
  const variantId = btn.dataset.variantId;
  const context = btn.dataset.context;

  let action; // 'add' or 'remove'

  if (context === 'pdp' && variantId) {
    if (Wishlist.hasVariant(productId, variantId)) {
      Wishlist.removeVariant(productId, variantId);
      action = 'remove';
    } else if (Wishlist.isPdpActive(productId, variantId)) {
      Wishlist.removeProduct(productId);
      action = 'remove';
    } else {
      Wishlist.addVariant({ productId, variantId, handle });
      action = 'add';
    }
  } else {
    if (Wishlist.hasProduct(productId)) {
      Wishlist.removeProduct(productId);
      action = 'remove';
    } else {
      Wishlist.addProduct({ productId, handle });
      action = 'add';
    }
  }

  document.dispatchEvent(new CustomEvent('wishlist:change', {
    detail: { action: action, button: btn }
  }));
}

  // Hook into Shopify's standard variant change event (most themes including Dawn)
  document.addEventListener('variant:change', (e) => {
    if (e.detail && e.detail.variant) {
      const variant = e.detail.variant;
      const productId = variant.product_id || e.detail.productId;
      if (!productId) return;
      document.querySelectorAll(`${SELECTOR}[data-context="pdp"][data-product-id="${productId}"]`).forEach(btn => {
        btn.dataset.variantId = variant.id;
        updateButton(btn);
      });
    }
  });

  // Fallback: watch URL changes (most themes update ?variant= in the URL on size selection)
  let lastUrl = location.href;
  new MutationObserver(() => {
    if (location.href !== lastUrl) {
      lastUrl = location.href;
      const params = new URLSearchParams(location.search);
      const variantId = params.get('variant');
      if (variantId) {
        document.querySelectorAll(`${SELECTOR}[data-context="pdp"]`).forEach(btn => {
          btn.dataset.variantId = variantId;
          updateButton(btn);
        });
      }
    }
  }).observe(document, { subtree: true, childList: true });

  // Wire up clicks and initial state
  document.addEventListener('click', handleClick);
  document.addEventListener('wishlist:change', updateAllButtons);
  document.addEventListener('DOMContentLoaded', updateAllButtons);

  // Also update immediately if DOM is already loaded
  if (document.readyState !== 'loading') updateAllButtons();
})();

/**
 * Header wishlist count badge updater
 */
(function() {
  function updateCount() {
    const count = Wishlist.count();
    document.querySelectorAll('[data-wishlist-count]').forEach(badge => {
      const numberSpan = badge.querySelector('[aria-hidden="true"]');
      if (numberSpan) numberSpan.textContent = count;
      badge.style.display = count > 0 ? '' : 'none';
    });
  }

  document.addEventListener('wishlist:change', updateCount);
  document.addEventListener('DOMContentLoaded', updateCount);

  if (document.readyState !== 'loading') updateCount();
})();

/**
 * Wishlist add feedback — heart pulse + toast notification
 */
(function() {
  let toastTimer = null;

  function showToast() {
    const toast = document.getElementById('wishlist-toast');
    if (!toast) return;

    if (toastTimer) clearTimeout(toastTimer);

    toast.classList.add('is-visible');
    toastTimer = setTimeout(() => {
      toast.classList.remove('is-visible');
    }, 3200);
  }

  function pulseButton(btn) {
    if (!btn) return;
    btn.classList.remove('is-pulsing');
    void btn.offsetWidth;
    btn.classList.add('is-pulsing');
    setTimeout(() => btn.classList.remove('is-pulsing'), 400);
  }

  document.addEventListener('wishlist:change', (e) => {
    if (!e.detail || e.detail.action !== 'add') return;
    pulseButton(e.detail.button);
    showToast();
  });
})();

/**
 * Customer sync layer — merges localStorage wishlist with server-side metafield
 * when logged in. Pushes updates to the server on every change.
 */
(function() {
  const WORKER_URL = 'https://modecraze-wishlist.outofmysight.workers.dev';
  let syncInFlight = false;
  let pendingSync = false;

  function isLoggedIn() {
    return window.modecraze?.customer?.loggedIn && window.modecraze?.customer?.wishlistToken;
  }

  function getAuthHeader() {
    return 'Bearer ' + window.modecraze.customer.wishlistToken;
  }

  /**
   * Load wishlist from server.
   */
  async function loadFromServer() {
    try {
      const res = await fetch(`${WORKER_URL}/wishlist/load`, {
        headers: { 'Authorization': getAuthHeader() }
      });
      if (!res.ok) {
        console.warn('[Wishlist sync] Load failed:', res.status);
        return null;
      }
      const data = await res.json();
      return Array.isArray(data.items) ? data.items : [];
    } catch (e) {
      console.warn('[Wishlist sync] Load error:', e);
      return null;
    }
  }

  /**
   * Save wishlist to server. Debounced: if a save is in flight,
   * we queue the next one to run after the current finishes.
   */
  async function saveToServer(items) {
    if (syncInFlight) {
      pendingSync = true;
      return;
    }
    syncInFlight = true;
    try {
      const res = await fetch(`${WORKER_URL}/wishlist/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': getAuthHeader()
        },
        body: JSON.stringify({ items })
      });
      if (!res.ok) {
        console.warn('[Wishlist sync] Save failed:', res.status);
      }
    } catch (e) {
      console.warn('[Wishlist sync] Save error:', e);
    } finally {
      syncInFlight = false;
      if (pendingSync) {
        pendingSync = false;
        // Re-run with latest local state
        saveToServer(Wishlist.getAll());
      }
    }
  }

  /**
   * Merge two wishlists. For duplicates (same productId + variantId),
   * keep the entry with the newer addedAt.
   */
  function mergeWishlists(localItems, serverItems) {
    const map = new Map();
    const key = (item) => `${item.productId}::${item.variantId === null ? 'null' : item.variantId}`;

    // Seed with server items
    serverItems.forEach(item => map.set(key(item), item));

    // Local items: overwrite if newer
    localItems.forEach(item => {
      const k = key(item);
      const existing = map.get(k);
      if (!existing || (item.addedAt || 0) > (existing.addedAt || 0)) {
        map.set(k, item);
      }
    });

    return Array.from(map.values());
  }

  /**
   * Write merged items back to localStorage directly (bypassing
   * Wishlist.write because we don't want to dispatch wishlist:change
   * — that would create a sync loop).
   */
  function writeLocalSilent(items) {
    try {
      localStorage.setItem('modecraze_wishlist', JSON.stringify(items));
    } catch (e) {
      console.warn('[Wishlist sync] Local write failed:', e);
    }
  }

  /**
   * Run the initial merge on page load if logged in.
   */
  async function initialSync() {
    if (!isLoggedIn()) return;

    const localItems = Wishlist.getAll();
    const serverItems = await loadFromServer();
    if (serverItems === null) return; // Network failure — keep local as-is

    const merged = mergeWishlists(localItems, serverItems);
    writeLocalSilent(merged);

    // Push merged result back to server so server matches local
    await saveToServer(merged);

    // Notify UI that wishlist may have changed (so hearts/badges refresh)
    document.dispatchEvent(new CustomEvent('wishlist:change', {
      detail: { action: 'sync', button: null }
    }));
  }

  /**
   * On every wishlist change while logged in, push to server.
   * (Skip if the change came from our own initial sync.)
   */
  document.addEventListener('wishlist:change', (e) => {
    if (!isLoggedIn()) return;
    if (e.detail?.action === 'sync') return;
    saveToServer(Wishlist.getAll());
  });

  // Run merge on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialSync);
  } else {
    initialSync();
  }
})();

/**
 * Logout detection — clear localStorage wishlist when customer logs out.
 *
 * Strategy: on every page load, store the current customer ID in sessionStorage.
 * If on a subsequent load we see no customer but sessionStorage had one,
 * a logout happened — wipe the wishlist.
 */
(function() {
  const SESSION_KEY = 'modecraze_last_known_customer_id';

  function checkForLogout() {
    const currentCustomerId = window.modecraze?.customer?.loggedIn
      ? String(window.modecraze.customer.id)
      : null;

    let lastKnownCustomerId = null;
    try {
      lastKnownCustomerId = sessionStorage.getItem(SESSION_KEY);
    } catch (e) {
      // sessionStorage unavailable — skip
      return;
    }

    // Logout detected: we had a customer last page load, now we don't
    if (lastKnownCustomerId && !currentCustomerId) {
      Wishlist.clear();
      try {
        sessionStorage.removeItem(SESSION_KEY);
      } catch (e) {}
      return;
    }

    // Update last-known
    try {
      if (currentCustomerId) {
        sessionStorage.setItem(SESSION_KEY, currentCustomerId);
      }
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkForLogout);
  } else {
    checkForLogout();
  }
})();